import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, ETAPAS, OPTOS, CENTROS, SUGERIDOS, PEND_LABELS, fmtDate, edadTexto, esRevision, soloRevision } from "@/lib/api";
import { useCentro } from "@/lib/centro";
import { useAuth, puedeEco } from "@/lib/auth";
import { PageTitle, StageBadge, TipoBadge, StatusPill } from "@/components/Badges";
import { PrintButton, PrintHeader } from "@/components/PrintList";

const PendingStrip = ({ items, active, onPick }) => (
  <div data-testid="pending-strip" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-10 gap-2">
    {items.map((p) => (
      <button key={p.id} data-testid={`pending-${p.id}`} onClick={() => onPick(active === p.id ? "" : p.id)}
        className={`lift text-left rounded-lg border px-3 py-2 transition-colors ${active === p.id ? "bg-amber-100 border-amber-400" : p.n ? "bg-amber-50 border-amber-200" : "bg-white border-slate-200"}`}>
        <div className={`font-[JetBrains_Mono] text-lg font-semibold ${p.n ? "text-amber-800" : "text-slate-300"}`}>{p.n}</div>
        <div className="text-[11px] leading-tight text-slate-600">{p.label}</div>
      </button>
    ))}
  </div>
);

const StageTabs = ({ etapa, setEtapa, counts }) => {
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  const tab = (id, label, n) => (
    <button key={id || "all"} data-testid={`stage-tab-${id || "todas"}`} onClick={() => setEtapa(id)}
      className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors whitespace-nowrap ${etapa === id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
      {label} <span className="font-[JetBrains_Mono] opacity-70 ml-1">{n}</span>
    </button>
  );
  return <div className="flex gap-2 overflow-x-auto pb-1">{[tab("", "Todas", total), ...ETAPAS.map((e) => tab(e.id, e.label, counts[e.id] || 0))]}</div>;
};

const sugLabel = (ids) => ids.map((i) => SUGERIDOS.find((s) => s.id === i)?.label || i).join(", ");

export default function Pacientes() {
  const { centro, setCentro } = useCentro();
  const isAdmin = puedeEco(useAuth().user);
  const [params, setParams] = useSearchParams();
  const etapa = params.get("etapa") || "";
  const pendiente = params.get("pendiente") || "";
  const [pend, setPend] = useState([]);
  const [q, setQ] = useState("");
  const [opto, setOpto] = useState("");
  const [rows, setRows] = useState(null);
  const [counts, setCounts] = useState({});
  const nav = useNavigate();

  useEffect(() => {
    api.get("/patients/counts", { params: { centro } }).then((r) => setCounts(r.data));
    api.get("/patients/pendientes", { params: { centro } }).then((r) => setPend(r.data));
  }, [centro]);
  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/patients", { params: { centro, etapa, q, optometrista: opto, pendiente } }).then((r) => setRows(r.data));
    }, 200);
    return () => clearTimeout(t);
  }, [centro, etapa, q, opto, pendiente]);

  return (
    <div data-testid="pacientes-page">
      <div className="no-print"><PageTitle title="Control de pacientes" subtitle="Flujo completo: evaluación → informe → presupuesto → respuesta → tratamiento"><Link to="/duplicados" data-testid="duplicados-link" className="inline-flex h-9 items-center rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-700 hover:border-slate-400">Revisar duplicados</Link><PrintButton /></PageTitle></div>
      <PrintHeader title={pendiente ? PEND_LABELS[pendiente] || "Pacientes" : "Control de pacientes"} detalle={[etapa && ETAPAS.find((e) => e.id === etapa)?.label, opto, q && `Búsqueda: ${q}`].filter(Boolean).join(" · ")} n={rows?.length} />
      <div className="space-y-3 mb-4 no-print">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-600">Tareas pendientes del proceso</h3>
        <PendingStrip items={isAdmin ? pend : pend.filter((x) => x.id !== "economico_pendiente")} active={pendiente} onPick={(k) => setParams(k ? { pendiente: k } : {})} />
        <StageTabs etapa={etapa} counts={counts} setEtapa={(e) => setParams(e ? { etapa: e } : {})} />
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search className="h-4 w-4 absolute left-3 top-2.5 text-slate-400" />
            <Input data-testid="patients-search" placeholder="Buscar por nombre o código…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-9 bg-white" />
          </div>
          <select data-testid="patients-centro-filter" className="field-select" style={{ width: 180 }} value={centro} onChange={(e) => setCentro(e.target.value)}>
            <option value="">Todos los centros</option>
            {CENTROS.map((c) => <option key={c} value={c}>{c === "MADRID" ? "Madrid" : "Alcobendas"}</option>)}
          </select>
          <select data-testid="patients-opto-filter" className="field-select" style={{ width: 200 }} value={opto} onChange={(e) => setOpto(e.target.value)}>
            <option value="">Todas las optometristas</option>
            {OPTOS.map((o) => <option key={o}>{o}</option>)}
          </select>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="patients-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 sticky top-0">
            <tr>
              {["Paciente", "Centro / Opto.", "Cita", "Examen", "Informe", "Presupuesto", "Resp. tratamiento", "Oído", "Hora terapia", ...(isAdmin ? ["Económico"] : []), "Etapa", "Tratamientos"].map((h) => (
                <th key={h} className="text-left font-semibold px-3 py-2.5 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows === null && <tr><td colSpan={12} className="p-6 text-center text-slate-400">Cargando…</td></tr>}
            {rows?.length === 0 && <tr><td colSpan={12} className="p-6 text-center text-slate-400" data-testid="patients-empty">No hay pacientes con estos filtros.</td></tr>}
            {rows?.map((p) => (
              <tr key={p.id} data-testid={`patient-row-${p.id}`} onClick={() => nav(`/pacientes/${p.id}`)} className="hover:bg-slate-50 cursor-pointer transition-colors">
                <td className="px-3 py-2.5 whitespace-nowrap">
                  <div className="font-medium text-slate-900">{p.nombre}{edadTexto(p) && <span className="text-xs text-slate-400 ml-1">({edadTexto(p)})</span>}{p.codigo && <span data-testid={`patient-codigo-${p.id}`} className="ml-2 font-[JetBrains_Mono] text-[11px] text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">{p.codigo}</span>}</div>
                  <div className="text-[11px] text-slate-400 max-w-[220px] truncate">{sugLabel(p.tratamientos_sugeridos)}</div>
                  {p.telefono && <div className="print-only text-[11px]">Tel. {p.telefono}</div>}
                  {p.pendientes.length > 0 && <div className="text-[11px] text-amber-700 font-medium" data-testid={`patient-pending-${p.id}`}>{p.pendientes.map((k) => PEND_LABELS[k]).join(" · ")}</div>}
                </td>
                <td className="px-3 py-2.5 text-slate-600 text-xs whitespace-nowrap">{p.centro || "—"}<div className="text-slate-400">{p.optometrista}</div></td>
                <td className="px-3 py-2.5 whitespace-nowrap"><StatusPill value={p.estado_cita} />{p.fecha_cita && <div className="text-[11px] text-slate-400 mt-0.5">{fmtDate(p.fecha_cita)}</div>}</td>
                <td className="px-3 py-2.5 text-slate-600 text-xs whitespace-nowrap">{p.tipo_examen || "—"}<div className="text-slate-400">{p.fecha_ee ? fmtDate(p.fecha_ee) : ""}</div></td>
                <td className="px-3 py-2.5"><StatusPill value={p.estado_informe} /></td>
                <td className="px-3 py-2.5"><StatusPill value={soloRevision(p) ? p.ppto_rev : p.envio_ppto} />{soloRevision(p) && <div className="text-[10px] text-slate-400 mt-0.5">revisión</div>}</td>
                <td className="px-3 py-2.5"><StatusPill value={soloRevision(p) ? p.respuesta_rev : p.respuesta} /></td>
                <td className="px-3 py-2.5"><StatusPill value={soloRevision(p) ? "" : p.respuesta_oido} /></td>
                <td className="px-3 py-2.5"><StatusPill value={soloRevision(p) ? "" : p.hora_terapia} />{!soloRevision(p) && p.dia_hora && <div className="text-[11px] text-slate-400 mt-0.5">{p.dia_hora}</div>}</td>
                {isAdmin && <td className="px-3 py-2.5"><StatusPill value={p.economico} /></td>}
                <td className="px-3 py-2.5"><StageBadge etapa={p.etapa} /></td>
                <td className="px-3 py-2.5"><div className="flex flex-wrap gap-1">{p.tratamientos.map((t, i) => <TipoBadge key={i} tipo={t.tipo} estado={t.estado} />)}</div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
